import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@fluentui/react-components";

import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from "@fluentui/react-components";

// Components
import Country from "../../../shared/components/Country";
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useSelectedSitesContext } from "../context/SelectedSiteContext";
import { useSitesQuery } from "../hooks/useSiteQueries";

const useStyles = makeStyles({
  container: {
    height: "calc(100dvh - 200px)", // adjust based on layout
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  scrollArea: {
    flex: 1,
    overflowY: "auto",
    overflowX: "auto",
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

const SitesDataGrid = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { value: selectedSites, setValue: setSelectedSites } = useSelectedSitesContext();

  // Errors
  const [errors, setErrors] = useState([]);

  const { data = [], isFetching, error, refetch } = useSitesQuery();

  // Effects
  useEffect(() => {
    const newErrors = [];
    if (error) {
      newErrors.push({ message: error?.message || t("generic.error"), type: "error", details: error });
    }
    if (newErrors.length > 0) {
      setErrors((prevErrors) => [...prevErrors, ...newErrors]);
    }
  }, [error, t]);

  if (isFetching) return <Spinner label={t("generic.loading")} />;

  const columns = [
    createTableColumn({
      columnId: "code",
      renderHeaderCell: () => "Code",
      renderCell: (item) => <TableCellLayout>{item.code}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "name",
      renderHeaderCell: () => t("generic.name"),
      renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "address",
      renderHeaderCell: () => t("generic.address"),
      renderCell: (item) => <TableCellLayout>{item.address}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "city",
      renderHeaderCell: () => t("generic.city"),
      renderCell: (item) => <TableCellLayout>{item.city}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "state",
      renderHeaderCell: () => t("generic.state"),
      renderCell: (item) => <TableCellLayout>{item.state}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "country",
      renderHeaderCell: () => t("generic.country"),
      renderCell: (item) => (
        <TableCellLayout>
          <Country code={item.country_code} />
        </TableCellLayout>
      ),
    }),
  ];

  const columnSizingOptions = {
    code: {
      minWidth: 40,
      defaultWidth: 40,
      idealWidth: 40,
    },
    address: {
      idealWidth: 400,
    },
  };

  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const newSelection = data.filter((l) => ids.includes(l.id));
    setSelectedSites(newSelection);
  };

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <DataGrid
        items={data}
        columns={columns}
        columnSizingOptions={columnSizingOptions}
        selectedItems={selectedSites.map((item) => item.id)}
        getRowId={(item) => item.id}
        focusMode="composite"
        resizableColumns
        selectionMode="single"
        onSelectionChange={handleSelectionChange}
      >
        <DataGridHeader>
          <DataGridRow
            className={styles.stickyHeaderRow}
            selectionCell={{
              checkboxIndicator: { "aria-label": "Select all rows" },
            }}
          >
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>

        <div className={styles.scrollArea}>
          <DataGridBody>
            {({ item, rowId }) => (
              <DataGridRow
                key={rowId}
                selectionCell={{
                  checkboxIndicator: { "aria-label": "Select row" },
                }}
              >
                {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
              </DataGridRow>
            )}
          </DataGridBody>
        </div>
      </DataGrid>
    </div>
  );
};

export default SitesDataGrid;
